// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * WI-895: detect the pharmacy slug from the URL.
 *
 * Two recognized patterns:
 *
 *   1. Subdomain (preferred for prod): `bayview.owlrxmu.owlconsultations.com`
 *      → slug = "bayview"
 *      Requires the wildcard DNS + SWA custom-domain config from sponsor.
 *      Cookies are scoped per-host so each pharmacy gets isolated session
 *      surface.
 *
 *   2. Path prefix (fallback, works without DNS): `/p/bayview/login`
 *      → slug = "bayview"
 *      Useful for local dev + immediate demo before subdomain DNS lands.
 *      All pharmacies share one cookie jar in this mode.
 *
 *   3. Neither → returns null. The login page renders generic OwlRx branding.
 *
 * Hostnames that DON'T count as pharmacy subdomains:
 *   - `owlrxmu.owlconsultations.com` itself (the apex)
 *   - `localhost` and `*.local` (dev environments)
 *   - `agreeable-ground-*.azurestaticapps.net` (raw SWA URL)
 *   - `claude-*.preview` etc.
 *
 * The list of "apex" hostnames is short and known; we hard-code it for
 * the deployed env and let everything else fall through.
 */

const APEX_HOSTNAMES = new Set([
  // Production apex
  'owlrx.owlconsultations.com',
  // Admin/platform console host — NOT a pharmacy (its leftmost label 'admin' must never be
  // parsed as a pharmacy subdomain of owlrx.owlconsultations.com).
  'admin.owlrx.owlconsultations.com',
  // Mockup apex (kept during the owlrxmu → owlrx transition)
  'owlrxmu.owlconsultations.com',
  // Azure SWA generated URL (and similar)
  'agreeable-ground-015595d1e.7.azurestaticapps.net',
])

// Subdomain labels that are infrastructure, never a pharmacy (belt-and-suspenders so e.g.
// admin.<apex> can never resolve to a "pharmacy" named after the label).
const RESERVED_SUBDOMAINS = new Set(['admin', 'www', 'api', 'app', 'platform'])

// The bare product apex hosts that serve the PUBLIC marketing landing page — NOT the admin console
// (admin.owlrx…), NOT a pharmacy, NOT dev. A pharmacy is still reachable here via the /p/{code} path.
const MARKETING_APEX_HOSTS = new Set([
  'owlrx.owlconsultations.com',
  'owlrxmu.owlconsultations.com',
])

/** True only on the bare product apex (the marketing front door). */
export function isApexMarketingHost(
  hostname: string = (typeof window === 'undefined' ? '' : window.location.hostname),
): boolean {
  return MARKETING_APEX_HOSTS.has(hostname)
}

const APEX_DOMAIN_SUFFIXES = [
  // Anything ending in .azurestaticapps.net but NOT in our APEX_HOSTNAMES is
  // also the apex. (e.g. preview environments.)
  '.azurestaticapps.net',
]

function isLocalhost(hostname: string): boolean {
  return hostname === 'localhost'
      || hostname === '127.0.0.1'
      || hostname.endsWith('.local')
      || hostname.endsWith('.localhost')
}

/**
 * Slug-shape: lowercase letters/digits/hyphens; 1..80 chars.
 * Rejects anything else so a stray hostname segment doesn't trigger a
 * pharmacy lookup.
 */
function looksLikeSlug(s: string): boolean {
  return /^[a-z0-9][a-z0-9-]{0,79}$/.test(s)
}

/**
 * Opaque pharmacy url_code shape: 16–32 lowercase hex chars (current codes are 20-hex / 80-bit).
 * Used to decide whether a `/p/{x}` identifier should resolve via the by-code endpoint (preferred)
 * vs the legacy slug endpoint. A name-derived slug effectively never matches (slugs carry non-hex
 * letters or hyphens), and a false positive is harmless — the caller falls back to the slug lookup.
 */
export function looksLikePharmacyCode(s: string): boolean {
  return /^[0-9a-f]{16,32}$/.test(s)
}

export interface PharmacyUrlResolution {
  /** "subdomain" | "path" | null */
  source: 'subdomain' | 'path' | null
  /** The slug from URL, or null if none. */
  slug: string | null
}

/** Read the current window's URL and resolve which (if any) pharmacy it points at. */
export function resolvePharmacyFromUrl(
  hostname: string = (typeof window === 'undefined' ? '' : window.location.hostname),
  pathname: string = (typeof window === 'undefined' ? '' : window.location.pathname),
): PharmacyUrlResolution {
  // Path-based check first: /p/{slug}/...
  const pathMatch = pathname.match(/^\/p\/([^/]+)/)
  if (pathMatch) {
    const candidate = pathMatch[1].toLowerCase()
    if (looksLikeSlug(candidate)) {
      return { source: 'path', slug: candidate }
    }
  }

  // Subdomain check
  if (!hostname || isLocalhost(hostname)) return { source: null, slug: null }
  if (APEX_HOSTNAMES.has(hostname)) return { source: null, slug: null }

  // For each apex domain suffix, see if the hostname is `<sub>.<apex>`.
  // We treat the LEFTMOST label as the slug.
  // Direct apex match for a non-listed apex domain → no pharmacy.
  for (const apex of APEX_HOSTNAMES) {
    if (hostname.endsWith('.' + apex)) {
      const sub = hostname.slice(0, hostname.length - apex.length - 1)
      // sub may itself contain dots ('foo.bar.apex.com'); only single-label slugs count.
      if (!sub.includes('.') && !RESERVED_SUBDOMAINS.has(sub) && looksLikeSlug(sub)) {
        return { source: 'subdomain', slug: sub }
      }
      return { source: null, slug: null }
    }
  }
  for (const suffix of APEX_DOMAIN_SUFFIXES) {
    if (hostname.endsWith(suffix)) {
      // Anything in azurestaticapps.net etc. — generated URL, not pharmacy.
      return { source: null, slug: null }
    }
  }

  // Unknown hostname (e.g. someone curled with a Host header). Return null
  // rather than guessing — the apex login still works.
  return { source: null, slug: null }
}

/**
 * Canonical origin where the pharmacy-facing app + SSO callback live.
 *
 * Pharmacy login / workspace URLs must use THIS host, never the platform
 * console host (e.g. admin.owlrx.owlconsultations.com) that a platform admin
 * may currently be browsing on. Using window.location.origin there produces a
 * login URL on the wrong host and breaks SSO (the pharmacy's Entra app
 * registers the owlrx callback as canonical; owlrxmu remains registered during
 * the migration window).
 *
 * In local dev / preview (localhost, *.azurestaticapps.net) we fall back to the
 * current origin so demos keep working without the production domain.
 */
const PHARMACY_APP_ORIGIN = 'https://owlrx.owlconsultations.com'

export function pharmacyAppOrigin(): string {
  if (typeof window === 'undefined') return PHARMACY_APP_ORIGIN
  const h = window.location.hostname
  if (isLocalhost(h) || h.endsWith('.azurestaticapps.net')) return window.location.origin
  return PHARMACY_APP_ORIGIN
}

/**
 * NEXT-1 (Mario 2026-08-07): validate a post-login return path carried through the login flow
 * as ?next=. Router state (state.from) dies at the two window.location.replace hard redirects
 * (apex /login → branded login; slug→code canonicalization), so the query string — which those
 * redirects DO preserve — is the durable carrier. Same-app RELATIVE paths only: anything else
 * (absolute URLs, protocol-relative //host, schemes, or a loop back into /login) is rejected —
 * a tampered next must never become an open redirect off the app.
 */
export function safeNextPath(raw: string | null | undefined): string | null {
  if (!raw) return null
  if (!raw.startsWith('/') || raw.startsWith('//')) return null
  if (raw.includes('://') || raw.includes('\\')) return null
  const path = raw.split('?')[0]
  if (path === '/login' || path.endsWith('/login')) return null
  return raw
}
